import React, { useEffect, useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import PolicyCard from '../../../components/cards/PolicyCard';
import { generateWordDoc } from '../../../utils/downloadHelpers';
import { getManagementRules } from '../../../services/policyAPIs';
import '../officePoliciesPage/style.css';
import { TextField } from '@mui/material';

// ==============================|| MANAGEMENT RULES PAGE ||============================== //

const ManagementRulesPage = () => {
  const [allExpanded, setAllExpanded] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [filters, setFilters] = useState([]);
  const [newFilter, setNewFilter] = useState('');
  const [title, setTitle] = useState('Management Rules');
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const printRef = useRef();
  const up = 'mr';

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const data = await getManagementRules();
        setPolicies(data);
      } catch (error) {
        console.error('Failed to fetch management rules', error);
      }
    };

    fetchPolicies();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Management Rules',
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        setAllExpanded(true);
        setTimeout(resolve, 200);
      });
    },
    onAfterPrint: () => setAllExpanded(false),
    pageStyle: `
      @page { size: A4; }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .office-policies-container {
          padding-top: 5px; 
          padding-bottom: 5px;
          padding-left: 3px;
          padding-right: 3px;
        }
      }
    `
  });

  const handleDownload = async () => {
    if (filteredPolicies.length === 0) {
      alert('There are no policies to download!');
      return;
    }

    await new Promise((resolve) => {
      setAllExpanded(true);
      setTimeout(resolve, 200);
    });

    const content = printRef.current;
    if (!content) {
      alert('Content not ready for download. Please try again.');
      setAllExpanded(false);
      return;
    }

    if (downloadFormat === 'pdf') {
      handlePrint();
    } else if (downloadFormat === 'word') {
      const htmlContent = content.outerHTML;
      generateWordDoc(htmlContent, title);
      setAllExpanded(false);
    }
  };

  const handleDelete = (id) => {
    setPolicies(policies.filter((policy) => policy.id !== id));
  };

  const handleAddFilter = () => {
    const trimmed = newFilter.trim();
    if (trimmed && !filters.includes(trimmed)) {
      setFilters((prev) => [...prev, trimmed]);
      setNewFilter('');
    }
  };

  const handleRemoveFilter = (keyword) => {
    setFilters((prev) => prev.filter((f) => f !== keyword));
  };

  const handleFilterClick = (keyword) => {
    setFilters((prevFilters) =>
      prevFilters.includes(keyword)
        ? prevFilters.filter((f) => f !== keyword)
        : [...prevFilters, keyword]
    );
  };

  const filteredPolicies =
    filters.length === 0
      ? policies
      : policies.filter((policy) =>
          filters.some(
            (keyword) =>
              policy.title?.toLowerCase().includes(keyword.toLowerCase()) ||
              policy.body?.toLowerCase().includes(keyword.toLowerCase())
          )
        );

  return (
    <>
      <div
        style={{
          marginBottom: '1rem',
          marginTop: '0.5rem',
          display: 'flex',
          flexDirection: 'row',
          gap: '15px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}
        className="office-policies-container"
      >
        <h3>Active Filters:</h3>
        {filters.map((filter) => (
          <div key={filter} style={{ position: 'relative', gap: '15px' }}>
            <button
              onClick={() => handleFilterClick(filter)}
              style={{
                padding: '6px 12px',
                backgroundColor: '#eee',
                border: '1px solid #ccc',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              {filter}
            </button>
            <button
              onClick={() => handleRemoveFilter(filter)}
              style={{
                position: 'absolute',
                top: '35%',
                left: '85%',
                cursor: 'pointer',
                background: 'transparent',
                border: 'none'
              }}
            >
              ❌
            </button>
          </div>
        ))}
        <TextField
          autoWidth
          label="Add a filter..."
          value={newFilter}
          onChange={(e) => setNewFilter(e.target.value)}
          variant="outlined"
          sx={{
            '& .MuiInputBase-root': {
              height: '30px',
            },
            '& .MuiInputBase-input': { padding: '10px 14px' },
            '& .MuiInputLabel-root': { top: '-10px' },
          }}
        />
        <button onClick={handleAddFilter} className="filter-button">
          Add
        </button>
      </div>

      <div ref={printRef}>
        {filteredPolicies.map((policy) => (
          <PolicyCard
            up={up}
            key={policy.id}
            id={policy.id}
            title={policy.title}
            body={policy.body}
            type={policy.type || 'MANAGEMENT_RULE'}
            onDelete={handleDelete}
            imageUrls={policy.imageUrls}
            forceExpanded={allExpanded}
          />
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'flex-start' }}>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
          <select
            className="styled-select"
            value={downloadFormat}
            onChange={(e) => setDownloadFormat(e.target.value)}
          >
            <option value="pdf">PDF</option>
            <option value="word">Word</option>
          </select>
          <button onClick={handleDownload} className="filter-button">
            Download
          </button>
        </div>
      </div>
    </>
  );
};

export default ManagementRulesPage;

